import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid'
import { Button } from '../ui/button'
import { Separator } from '../ui/separator'
import { Label } from '../ui/Label'
import { Input } from '../ui/Input'
import Field from '../ui/Field'
import SimpleComboBox from '../SimpleComboBox'
import { Description } from '../ui/Description'
import FieldStatusMessage from '../ui/FieldStatusMessage'
import { RadioGroup, RadioGroupItem } from '../ui/RadioGroup'
import { useController, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import _ from 'lodash'
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group'
import DatePicker from '../ui/DatePicker'
import * as yup from 'yup'
import { formatDate, getDate, getMonth } from 'date-fns'
import useCreateCustomTransactionRepeat from '@/services/api/cash-flow/transaction/useCreateCustomTransactionRepeat'
import useGetTransactionRepeatOptions from '@/services/api/cash-flow/transaction/useGetTransactionRepeatOptions'

const frequencyTypeOptions = [
  { id: 'Daily', name: 'Day' },
  { id: 'Weekly', name: 'Week' },
  { id: 'Monthly', name: 'Month' },
  { id: 'Annually', name: 'Year' },
] as const

const monthlyWeekDayOptions = [
  { id: 1, name: 'Every First' },
  { id: 2, name: 'Every Second' },
  { id: 3, name: 'Every Third' },
  { id: 4, name: 'Every Fourth' },
  { id: 5, name: 'Every Last' },
]

export default function CustomTransactionRepeat({
  open,
  onOpenChange,
  transactionDate,
  recurringSettingId = undefined,
}) {
  const { action, status } = useCreateCustomTransactionRepeat()


  const { data: recurringOptions } = useGetTransactionRepeatOptions({
    date: formatDate(transactionDate, 'yyyy-MM-dd'),
    recurring_setting_id: recurringSettingId,

    enabled: !!recurringSettingId,
  })

  const selectedSettingsOption = recurringOptions?.find((x) => x.is_custom)

  const yupSchema = yup
    .object()
    .noUnknown(true) // validate keys
    .shape({
      frequencyType: yup.string().nullable().required(),
      frequency: yup.number().positive().min(1).required(),
      stopType: yup.string().default('never').required(),
    })
    .when((value, schema) => {
      if (value[0].stopType === 'date') {
        return schema.shape({
          stopDate: yup.date().required(),
        })
      }

      if (value[0].stopType === 'endsAfterTimes') {
        return schema.shape({
          endsAfterTimes: yup.number().positive().min(1).required(),
        })
      }
    })
    .when((value, schema) => {
      if (value[0].frequencyType === 'Weekly') {
        return schema.shape({
          weekDays: yup.array().required().min(1),
        })
      }

      if (value[0].frequencyType === 'Monthly') {
        return schema
          .shape({
            monthlyOption: yup.string().required().label('Monthly Option'),
          })
          .when((data, schemaMonthly) => {
            if (data[0].monthlyOption === 'date') {
              return schemaMonthly.shape({
                monthlyDate: yup.number().positive().min(1).max(31),
              })
            }
            if (data[0].monthlyOption === 'dayOfTheMonth') {
              return schemaMonthly.shape({
                monthlyWeekDay: yup.string().required(),
                monthlyWeekNumber: yup
                  .object({ id: yup.number(), name: yup.string() })
                  .required(),
              })
            }
            return schemaMonthly
          })
      }

      return schema
    })

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    register,
  } = useForm({
    // resolver: zodResolver(schema),
    resolver: yupResolver(yupSchema),
    defaultValues: {
      frequencyType: selectedSettingsOption?.frequency_type ?? 'Daily',
      frequency: selectedSettingsOption?.frequency ?? 1,
      weekDays:
        selectedSettingsOption?.frequency_type === 'Weekly'
          ? selectedSettingsOption?.settings?.week_days.map((x) =>
              _.startCase(x.toLowerCase()),
            )
          : [],
      monthlyOption: selectedSettingsOption?.settings?.days
        ? 'date'
        : selectedSettingsOption?.settings?.week_days
          ? 'dayOfTheMonth'
          : null,
      monthlyDate: selectedSettingsOption?.settings?.days
        ? selectedSettingsOption?.settings.days[0]
        : '1', //parseInt?
      monthlyWeekDay:
        selectedSettingsOption?.frequency_type === 'Monthly' &&
        selectedSettingsOption?.settings?.week_days
          ? _.startCase(
              selectedSettingsOption?.settings.week_days[0]?.toLowerCase(),
            )
          : null, // Uppercase lowercaes issue?
      monthlyWeekNumber:
        monthlyWeekDayOptions.find(
          (x) => x.id === parseInt(selectedSettingsOption?.settings?.week),
        ) ?? null,
      stopType: selectedSettingsOption?.ends_on
        ? 'date'
        : selectedSettingsOption?.ends_after_times
          ? 'endsAfterTimes'
          : 'never',
      stopDate: selectedSettingsOption?.ends_on
        ? new Date(selectedSettingsOption?.ends_on)
        : new Date(),
      endsAfterTimes: selectedSettingsOption?.ends_after_times ?? 1,
    },
  })

  const { field: stopTypeField } = useController({
    control,
    name: 'stopType',
  })

  const { field: frequencyTypeField } = useController({
    control,
    name: 'frequencyType',
  })

  const { field: weekDaysField } = useController({
    control,
    name: 'weekDays',
    shouldUnregister: true,
  })

  const { field: monthlyWeekDayField } = useController({
    control,
    name: 'monthlyWeekDay',
  })

  const { field: monthlyWeekNumberField } = useController({
    control,
    name: 'monthlyWeekNumber',
  })

  const { field: monthlyOptionField } = useController({
    control,
    name: 'monthlyOption',
  })

  const { field: stopDateField } = useController({
    control,
    name: 'stopDate',
  })

  const frequency = useWatch({
    control,
    name: 'frequency',
  })

  const repeatType = useWatch({
    control,
    name: 'frequencyType',
  })

  const monthlyOption = useWatch({
    control,
    name: 'monthlyOption',
  })

  const endsAfterTimes = useWatch({
    control,
    name: 'endsAfterTimes',
  })

  function updateCoundBy(val, field) {
    let currentValue = getValues(field)
    const updatedValue =
      Number.isNaN(currentValue) || currentValue.toString() === ''
        ? 1
        : parseFloat(currentValue.toString()) + val
    setValue(field, updatedValue.toString(), { shouldValidate: true })
  }

  async function onSubmit(val) {
    const transactionDay = getDate(transactionDate)
    const weekDays = val?.weekDays
      ? val?.weekDays
      : val?.monthlyWeekDay
        ? [val?.monthlyWeekDay]
        : undefined

    const days = val?.monthlyDate
      ? [val.monthlyDate]
      : val?.frequencyType === 'Annually'
        ? [transactionDay]
        : undefined
    const week = val?.monthlyWeekNumber ? val.monthlyWeekNumber.id : undefined

    const month =
      val?.frequencyType === 'Annually'
        ? getMonth(transactionDate) + 1
        : undefined

    const payload = {
      transaction_date: formatDate(new Date(transactionDate), 'yyyy-MM-dd'),
      rid: recurringSettingId,
      recurring_settings: {
        frequency_type: val?.frequencyType ?? undefined,
        frequency: val?.frequency ?? undefined,
        ends_on: val?.stopDate
          ? formatDate(val.stopDate, 'yyyy-MM-dd')
          : undefined,
        ends_after_times: val?.endsAfterTimes ?? undefined,

        settings: {
          week_days: weekDays,
          days,
          week,
          month,
        },
      },
    }

    await action(payload)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onSubmit={handleSubmit(onSubmit)}
        className="h-[37.5rem] max-h-[37.5rem]"
        as="form"
      >
        <DialogHeader>
          <DialogTitle>Custom Transaction Repeat</DialogTitle>
          <DialogClose>
            <XMarkIcon />
          </DialogClose>
        </DialogHeader>
        <Separator />
        <DialogBody className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <div className="rounded-md border border-light">
              <Label className="bg-gray-low px-3 py-2 text-txt-secondary">
                Period
              </Label>
              <div className="p-3">
                <div className="flex flex-col gap-field-gap">
                  <Field
                    status={errors.frequency?.message ? 'error' : undefined}
                    statusText={errors.frequency?.message}
                  >
                    <Label className="">Repeat every</Label>
                    <div data-slot="control" className="!mt-3 flex gap-4">
                      <div className="flex gap-2">
                        <div className="w-12 ">
                          <Input
                            type="number"
                            className="text-right"
                            {...register('frequency')}
                          />
                        </div>
                        <div className="flex flex-col justify-between gap-1">
                          <Button
                            type="button"
                            iconOnly
                            size="sm"
                            variant="ghost"
                            className="size-4 rounded-full"
                            onClick={() => updateCoundBy(1, 'frequency')}
                          >
                            <ChevronUpIcon />
                          </Button>
                          <Button
                            disabled={parseInt(frequency) <= 1}
                            type="button"
                            iconOnly
                            size="sm"
                            variant="ghost"
                            className="size-4 rounded-full"
                            onClick={() => updateCoundBy(-1, 'frequency')}
                          >
                            <ChevronDownIcon />
                          </Button>
                        </div>
                      </div>
                      <div className="w-32">
                        <Field>
                          <SimpleComboBox
                            // items={frequencyTypeOptions.map((x) => x.id)}
                            items={frequencyTypeOptions.map((x) => x.id)}
                            selected={frequencyTypeField.value}
                            setSelected={(e) => {
                              frequencyTypeField.onChange(e)
                            }}
                            getOptionLabel={(item) => (
                              <div>
                                <p>
                                  {
                                    frequencyTypeOptions.find(
                                      (x) => x.id === item,
                                    )?.name
                                  }
                                  {parseInt(frequency) > 1 ? 's' : null}
                                </p>
                              </div>
                            )}
                            getSelectedLabel={(item) =>
                              `${
                                frequencyTypeOptions.find((x) => x.id === item)
                                  ?.name
                              }${parseInt(frequency) > 1 ? 's' : ''}`
                            }
                            getValue={(item) => item}
                            searchable={false}
                          />
                        </Field>
                      </div>
                    </div>

                    <FieldStatusMessage />
                  </Field>
                  <FieldStatusMessage
                    status={errors.frequencyType?.message ? 'error' : undefined}
                    statusText={`FrequencyType: ${errors.frequencyType?.message?.toString()}`}
                  />

                  {['Weekly', 'Monthly'].includes(repeatType) && (
                    <div>
                      <Field className="">
                        <Label>On</Label>
                        <div
                          data-slot="control"
                          className="flex flex-col gap-field-gap"
                        >
                          {/* Day */}
                          {repeatType === 'Weekly' && (
                            <div>
                              <ToggleGroup
                                size="sm"
                                type="multiple"
                                value={weekDaysField.value}
                                onValueChange={(val) => {
                                  weekDaysField.onChange(val)
                                }}
                              >
                                <ToggleGroupItem value="Mon">
                                  Mon
                                </ToggleGroupItem>
                                <ToggleGroupItem value="Tue">
                                  Tue
                                </ToggleGroupItem>
                                <ToggleGroupItem value="Wed">
                                  Wed
                                </ToggleGroupItem>
                                <ToggleGroupItem value="Thu">
                                  Thu
                                </ToggleGroupItem>
                                <ToggleGroupItem value="Fri">
                                  Fri
                                </ToggleGroupItem>
                                <ToggleGroupItem value="Sat">
                                  Sat
                                </ToggleGroupItem>
                                <ToggleGroupItem value="Sun">
                                  Sun
                                </ToggleGroupItem>
                              </ToggleGroup>

                              <FieldStatusMessage
                                isolated
                                status={
                                  errors.weekDays?.message ? 'error' : undefined
                                }
                                statusText={errors.weekDays?.message}
                              />
                            </div>
                          )}

                          {/* Month */}
                          {repeatType === 'Monthly' && (
                            <div>
                              <RadioGroup
                                className="gap-field-gap"
                                value={monthlyOptionField.value}
                                onValueChange={(val) => {
                                  monthlyOptionField.onChange(val)
                                }}
                              >
                                <Field>
                                  <RadioGroupItem value="date" />
                                  <Label>Date</Label>
                                </Field>
                                {/* <div> */}
                                {monthlyOption === 'date' && (
                                  <div>
                                    <div className="flex gap-2">
                                      <div className="w-12 ">
                                        <Input
                                          type="number"
                                          className="text-right"
                                          {...register('monthlyDate')}
                                        />
                                      </div>
                                      <div className="flex flex-col justify-between gap-1">
                                        <Button
                                          type="button"
                                          iconOnly
                                          size="sm"
                                          variant="ghost"
                                          className="size-4 rounded-full"
                                          onClick={() => {
                                            updateCoundBy(1, 'monthlyDate')
                                          }}
                                        >
                                          <ChevronUpIcon />
                                        </Button>
                                        <Button
                                          disabled={
                                            parseInt(
                                              getValues(
                                                'monthlyDate',
                                              ).toString(),
                                            ) <= 1
                                          }
                                          type="button"
                                          iconOnly
                                          size="sm"
                                          variant="ghost"
                                          className="size-4 rounded-full"
                                          onClick={() =>
                                            updateCoundBy(-1, 'monthlyDate')
                                          }
                                        >
                                          <ChevronDownIcon />
                                        </Button>
                                      </div>
                                    </div>
                                    <FieldStatusMessage
                                      status={
                                        errors.monthlyDate?.message
                                          ? 'error'
                                          : undefined
                                      }
                                      statusText={errors.monthlyDate?.message.toString()}
                                    />
                                    <Description>
                                      If the selected date doesn't exist in the
                                      month, the transaction will occur on the
                                      last day.
                                    </Description>
                                  </div>
                                )}

                                {/* </div> */}

                                <Field>
                                  <RadioGroupItem value="dayOfTheMonth" />
                                  <Label>Day of the week</Label>
                                </Field>

                                {monthlyOption === 'dayOfTheMonth' && (
                                  <>
                                    <div>
                                      <SimpleComboBox
                                        items={monthlyWeekDayOptions}
                                        selected={monthlyWeekNumberField.value}
                                        setSelected={
                                          monthlyWeekNumberField.onChange
                                        }
                                        getOptionLabel={(item) => item?.name}
                                        getSelectedLabel={(item) => item?.name}
                                        getValue={(item) => item?.id}
                                        searchable={false}
                                      />
                                      <FieldStatusMessage
                                        isolated
                                        status={
                                          errors.monthlyWeekNumber?.message
                                            ? 'error'
                                            : undefined
                                        }
                                        statusText={
                                          errors.monthlyWeekNumber
                                            ?.message as string
                                        }
                                      />
                                    </div>
                                    <div>
                                      <ToggleGroup
                                        key={repeatType}
                                        size="sm"
                                        type="single"
                                        value={monthlyWeekDayField.value}
                                        onValueChange={(val) => {
                                          monthlyWeekDayField.onChange(val)
                                        }}
                                      >
                                        <ToggleGroupItem value="Mon">
                                          Mon
                                        </ToggleGroupItem>
                                        <ToggleGroupItem value="Tue">
                                          Tue
                                        </ToggleGroupItem>
                                        <ToggleGroupItem value="Wed">
                                          Wed
                                        </ToggleGroupItem>
                                        <ToggleGroupItem value="Thu">
                                          Thu
                                        </ToggleGroupItem>
                                        <ToggleGroupItem value="Fri">
                                          Fri
                                        </ToggleGroupItem>
                                        <ToggleGroupItem value="Sat">
                                          Sat
                                        </ToggleGroupItem>
                                        <ToggleGroupItem value="Sun">
                                          Sun
                                        </ToggleGroupItem>
                                      </ToggleGroup>
                                      <FieldStatusMessage
                                        status={
                                          errors.monthlyWeekDay?.message
                                            ? 'error'
                                            : undefined
                                        }
                                        statusText={errors.monthlyWeekDay?.message.toString()}
                                      />
                                    </div>
                                  </>
                                )}
                              </RadioGroup>
                              <FieldStatusMessage
                                isolated
                                status={
                                  errors.monthlyOption?.message
                                    ? 'error'
                                    : undefined
                                }
                                statusText={errors.monthlyOption?.message.toString()}
                              />
                            </div>
                          )}
                        </div>
                      </Field>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-md border border-light">
              <Label className="bg-gray-low px-3 py-2 text-txt-secondary">
                Stop Period
              </Label>

              <div className="p-3">
                <RadioGroup
                  value={stopTypeField.value}
                  onValueChange={stopTypeField.onChange}
                >
                  <Field>
                    <RadioGroupItem value="never" />
                    <Label>Never</Label>
                  </Field>
                  <div className="flex flex-col gap-field-gap">
                    <Field>
                      <RadioGroupItem value="date" />
                      <Label>Date</Label>
                    </Field>
                    {stopTypeField.value === 'date' && (
                      <DatePicker
                        value={stopDateField.value}
                        selected={new Date(stopDateField.value)}
                        onSelect={(val) =>
                          stopDateField.onChange(new Date(val))
                        }
                        disabled={{ before: new Date() }}
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-field-gap">
                    <Field>
                      <RadioGroupItem value="endsAfterTimes" />
                      <Label>After Repetitions</Label>
                    </Field>
                    {stopTypeField.value === 'endsAfterTimes' && (
                      <div>
                        <div className="flex gap-2">
                          <div className="w-12 ">
                            <Input
                              type="number"
                              className="text-right"
                              {...register('endsAfterTimes')}
                            />
                          </div>
                          <div className="flex flex-col justify-between gap-1">
                            <Button
                              type="button"
                              iconOnly
                              size="sm"
                              variant="ghost"
                              className="size-4 rounded-full"
                              onClick={() => updateCoundBy(1, 'endsAfterTimes')}
                            >
                              <ChevronUpIcon />
                            </Button>
                            <Button
                              disabled={parseInt(endsAfterTimes) <= 1}
                              type="button"
                              iconOnly
                              size="sm"
                              variant="ghost"
                              className="size-4 rounded-full"
                              onClick={() =>
                                updateCoundBy(-1, 'endsAfterTimes')
                              }
                            >
                              <ChevronDownIcon />
                            </Button>
                          </div>
                        </div>
                        <FieldStatusMessage
                          isolated
                          status={
                            errors.endsAfterTimes?.message ? 'error' : undefined
                          }
                          statusText={errors.endsAfterTimes?.message.toString()}
                        />
                      </div>
                    )}
                  </div>
                </RadioGroup>
                <FieldStatusMessage
                  isolated
                  status={errors.stopType?.message ? 'error' : undefined}
                  statusText={errors.stopType?.message.toString()}
                />
              </div>
            </div>
          </div>
        </DialogBody>
        <Separator />
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </DialogClose>

          <Button loading={status.isPending}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
